import {
  type AltchaWidgetElement,
  makeAltchaBoxClickable,
  setupAltcha,
  waitForAltcha,
} from "./altcha";
import { errorText } from "./error-messages";

export const initPhoneSmsVerification = () => {
  const phoneVerification = document.getElementById("phone-verification") as HTMLDivElement;

  if (!phoneVerification) {
    return;
  }

  setupAltcha();

  const altchaWrapper = phoneVerification.querySelector(
    ".phone-enter .altcha-wrapper",
  ) as HTMLDivElement | null;
  const altchaWidget = altchaWrapper?.querySelector("altcha-widget") as AltchaWidgetElement | null;

  if (altchaWidget) {
    makeAltchaBoxClickable(altchaWidget);
  }

  const resultDiv = phoneVerification.querySelector(
    ".verifications-form-phone-result",
  ) as HTMLDivElement;

  const phoneVerified = phoneVerification.querySelector(".phone-verified") as HTMLDivElement;
  const phoneVerify = phoneVerification.querySelector(".phone-verify") as HTMLDivElement;
  const phoneNotVerified = phoneVerification.querySelector(".phone-not-verified") as HTMLDivElement;

  const spinner = resultDiv.querySelector(".spinner") as HTMLDivElement;

  // Phone enter
  const phoneEnterWrapper = phoneVerification.querySelector(".phone-enter") as HTMLDivElement;

  // Phone verification
  const verificationCodeSent = phoneVerification.querySelector(
    ".verification-code-sent",
  ) as HTMLParagraphElement;
  const verificationCodeSentAgain = phoneVerification.querySelector(
    ".verification-code-sent-again",
  ) as HTMLParagraphElement;

  // Errors
  const errorsWrapper = phoneVerification.querySelector(".errors") as HTMLDivElement;
  const unavailableServiceError = errorsWrapper.querySelector(
    ".errors .error-phone-unavailable-service",
  ) as HTMLDivElement;
  const limitVerificationError = errorsWrapper.querySelector(
    ".errors .error-limit-verifications",
  ) as HTMLDivElement;

  const hideSiblingsElements = (element: HTMLElement) => {
    const parentNode = element.parentNode as HTMLElement;
    const descendants = parentNode.children;

    const siblings = Array.prototype.filter.call(descendants, (sibling) => sibling !== element);

    siblings.forEach((element) => {
      element.classList.add("d-none");
    });
  };

  const hideAllMessages = () => {
    spinner.classList.add("d-none");
    errorsWrapper.classList.add("d-none");
    phoneVerified.classList.add("d-none");
    phoneVerify.classList.add("d-none");
    phoneNotVerified.classList.add("d-none");
  };

  // Strip non-numeric characters from the verification code input
  const verificationCodeInput = phoneVerification.querySelector(
    "input.verification_phone_code",
  ) as HTMLInputElement;
  verificationCodeInput.addEventListener("input", () => {
    verificationCodeInput.value = verificationCodeInput.value.replace(/\D/g, "").slice(0, 6);
  });

  const setPhoneNumber = (phoneNumber: string) => {
    const phoneNumberCollection: NodeListOf<HTMLDivElement> =
      phoneVerification.querySelectorAll(".phone-number");

    phoneNumberCollection.forEach((element) => {
      element.innerText = phoneNumber;
    });
  };

  function addPhone(altchaPayload: string) {
    const phoneInput = phoneVerification.querySelector(".phone-enter input") as HTMLInputElement;
    const phone = phoneInput.value;

    const phonePrefixInput = phoneVerification.querySelector(
      ".phone-enter .phone_prefix",
    ) as HTMLInputElement;
    const phonePrefix = phonePrefixInput.value;

    const csrfInput = phoneVerification.querySelector(
      '.phone-enter input[name="_csrf_token"]',
    ) as HTMLInputElement | null;

    const formData = new FormData();
    formData.append("phone", phone);
    formData.append("phone_prefix", phonePrefix);
    formData.append("altcha", altchaPayload);
    formData.append("_csrf_token", csrfInput?.value ?? "");

    fetch("/website-api/phone/", {
      credentials: "same-origin",
      method: "post",
      body: formData,
    })
      .then((response) => {
        if (response.status === 401) {
          spinner.classList.add("d-none");

          // Unauthorized
          window.location.href = "/";

          throw new Error("Unauthorized");
        }

        if (response.status === 429) {
          spinner.classList.add("d-none");

          // Prefer the specific message from the response body (e.g. the phone-number
          // limit); fall back to the generic rate-limit text (route-level throttle,
          // which has no response envelope).
          return response
            .json()
            .catch(() => null)
            .then((jsonResponse) => {
              alert(jsonResponse?.metadata?.error_message ?? errorText("rateLimited"));

              throw new Error("Too many requests");
            });
        }

        if (response.status === 500) {
          spinner.classList.add("d-none");

          // HTTP_INTERNAL_SERVER_ERROR: tell user service is unavailable
          errorsWrapper.classList.remove("d-none");
          hideSiblingsElements(errorsWrapper);

          unavailableServiceError.classList.remove("d-none");
          hideSiblingsElements(unavailableServiceError);

          throw new Error("Service is unavailable");
        }

        return response.json();
      })
      .then((jsonResponse) => {
        if (jsonResponse.metadata.code >= 400) {
          spinner.classList.add("d-none");

          if (jsonResponse.metadata.errors) {
            alert(jsonResponse.metadata.errors[0].error);

            return;
          }

          if (jsonResponse.metadata.error_message) {
            alert(jsonResponse.metadata.error_message);

            return;
          }

          alert(errorText("generic"));

          return;
        }

        phoneEnterWrapper.classList.add("d-none");

        fetch("/website-api/phone/verification/", {
          credentials: "same-origin",
          method: "get",
        })
          .then((response) => {
            if (response.status === 401) {
              window.location.href = "/";

              throw new Error("Unauthorized");
            }

            return response.json();
          })
          .then((jsonResponse) => {
            spinner.classList.add("d-none");

            setPhoneNumber(jsonResponse.data.phone);

            /* Phone number is verified, display phone number is verified */
            if (jsonResponse.data.verified === true) {
              phoneVerified.classList.remove("d-none");
              hideSiblingsElements(phoneNotVerified);

              return;
            }

            phoneVerify.classList.remove("d-none");
            hideSiblingsElements(phoneVerify);

            if (jsonResponse.data.attempts === 1) {
              verificationCodeSent.classList.remove("d-none"); // first message
              verificationCodeSentAgain.classList.add("d-none");

              return;
            }

            verificationCodeSentAgain.classList.remove("d-none"); // retry message
            verificationCodeSent.classList.add("d-none");
          })
          .catch(() => {});
      })
      .catch(() => {});
  }

  function verifyNumber() {
    resultDiv.classList.remove("d-none");
    spinner.classList.remove("d-none");
    hideSiblingsElements(spinner);

    const formData = new FormData();

    fetch("/website-api/phone/verification/", {
      credentials: "same-origin",
      method: "post",
      body: formData,
    })
      .then((response) => {
        if (response.status === 401) {
          //Unauthorized
          window.location.href = "/";

          throw new Error("Unauthorized");
        }

        if (response.status === 429) {
          resultDiv.classList.remove("d-none");
          phoneNotVerified.classList.remove("d-none");
          spinner.classList.add("d-none");

          alert(errorText("rateLimited"));

          throw new Error("Too many requests");
        }

        if (response.status === 500) {
          errorsWrapper.classList.remove("d-none");
          hideSiblingsElements(errorsWrapper);

          unavailableServiceError.classList.remove("d-none");
          hideSiblingsElements(unavailableServiceError);

          throw new Error("Service is unavailable");
        }

        return response.json();
      })
      .then(() => {
        fetch("/website-api/phone/verification/", {
          credentials: "same-origin",
          method: "get",
        })
          .then((response) => {
            if (response.status === 401) {
              //Unauthorized
              window.location.href = "/";

              throw new Error("Unauthorized");
            }

            return response.json();
          })
          .then((jsonResponse) => {
            spinner.classList.add("d-none");
            phoneEnterWrapper.classList.add("d-none");

            setPhoneNumber(jsonResponse.data.phone);

            /* Phone number is verified, display phone number is verified */
            if (jsonResponse.data.verified === true) {
              phoneVerified.classList.remove("d-none");
              hideSiblingsElements(phoneNotVerified);

              return;
            }

            phoneVerify.classList.remove("d-none");
            hideSiblingsElements(phoneVerify);

            // Type SMS:
            if (jsonResponse.data.attempts === 1) {
              verificationCodeSent.classList.remove("d-none"); // first message
              verificationCodeSentAgain.classList.add("d-none");

              return;
            }

            verificationCodeSentAgain.classList.remove("d-none"); // retry message
            verificationCodeSent.classList.add("d-none");
          })
          .catch(() => {});
      })
      .catch(() => {});
  }

  const phoneAddBtn = phoneVerification.querySelector(".phone-add") as HTMLButtonElement;

  phoneAddBtn.addEventListener("click", (e) => {
    e.preventDefault();

    if (!altchaWrapper || !altchaWidget) {
      return;
    }

    phoneAddBtn.disabled = true;

    waitForAltcha(altchaWrapper, altchaWidget)
      .then((payload) => {
        resultDiv.classList.remove("d-none");
        spinner.classList.remove("d-none");
        hideSiblingsElements(spinner);

        addPhone(payload);
      })
      .catch(() => {})
      .finally(() => {
        phoneAddBtn.disabled = false;
      });
  });

  const phoneVerifySms = phoneVerification.querySelector(".phone-verify-sms") as HTMLButtonElement;

  phoneVerifySms.addEventListener("click", (e) => {
    e.preventDefault();

    verifyNumber();
  });

  const phoneRemoveCollection: NodeListOf<HTMLButtonElement> =
    document.querySelectorAll(".phone-remove");

  phoneRemoveCollection.forEach((element) => {
    element.addEventListener("click", phoneRemoveHandler);
  });

  function phoneRemoveHandler() {
    spinner.classList.remove("d-none");

    fetch("/website-api/user/phone/", {
      credentials: "same-origin",
      method: "delete",
    })
      .then((response) => {
        if (response.status === 401) {
          window.location.href = "/";

          throw new Error("Unauthorized");
        }

        if (response.status === 429) {
          spinner.classList.add("d-none");

          alert(errorText("rateLimited"));

          throw new Error("Too many attempts");
        }

        return response.json();
      })
      .then(() => {
        spinner.classList.add("d-none");
        resultDiv.classList.add("d-none");

        const phoneVerificationInput = phoneVerification.querySelector(
          ".phone-enter input",
        ) as HTMLInputElement;
        phoneVerificationInput.value = "";
        phoneEnterWrapper.classList.remove("d-none");
      })
      .catch(() => {});
  }

  const phoneVerifyEnterCode = phoneVerification.querySelector(
    ".phone-verify-enter-code",
  ) as HTMLButtonElement;

  phoneVerifyEnterCode.addEventListener("click", () => {
    const verificarionPhoneCodeInput = document.querySelector(
      "input.verification_phone_code",
    ) as HTMLInputElement;

    const verificationPhoneCode = verificarionPhoneCodeInput.value;

    spinner.classList.remove("d-none");

    const formData = `verification_phone_code=${verificationPhoneCode}`;

    fetch("/website-api/phone/verification/", {
      credentials: "same-origin",
      method: "PATCH",
      body: formData,
      headers: {
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
      },
    })
      .then((response) => {
        if (response.status === 401) {
          // Unauthorized
          window.location.href = "/";

          throw new Error("Unauthorized");
        }

        if (response.status === 429) {
          spinner.classList.add("d-none");

          alert(errorText("rateLimited"));

          throw new Error("Too many requests");
        }

        return response.json();
      })
      .then((jsonResponse) => {
        if (jsonResponse.metadata.code >= 400) {
          spinner.classList.add("d-none");
          if (jsonResponse.metadata.errors) {
            alert(jsonResponse.metadata.errors[0].error);

            return;
          }

          if (jsonResponse.metadata.error_message) {
            alert(jsonResponse.metadata.error_message);

            return;
          }

          alert(errorText("generic"));

          return;
        }

        hideAllMessages();

        if (jsonResponse.metadata.code === 200) {
          fetch("/website-api/phone/verification/", {
            credentials: "same-origin",
            method: "get",
          })
            .then((response) => {
              if (response.status === 401) {
                window.location.href = "/";

                throw new Error("Unauthorized");
              }

              return response.json();
            })
            .then((jsonResponse) => {
              spinner.classList.add("d-none");

              setPhoneNumber(jsonResponse.data.phone);

              phoneVerified.classList.remove("d-none");
            })
            .catch(() => {});

          setPhoneNumber(jsonResponse.data.phone);

          phoneVerified.classList.remove("d-none");
        }

        phoneEnterWrapper.classList.add("d-none");
      })
      .catch(() => {});
  });

  const cancelBtn = document.querySelector(".phone-verify .cancel") as HTMLButtonElement;

  cancelBtn.addEventListener("click", () => {
    spinner.classList.remove("d-none");

    fetch("/website-api/phone/verification/", {
      credentials: "same-origin",
      method: "get",
    })
      .then((response) => {
        if (response.status === 401) {
          window.location.href = "/";

          throw new Error("Unauthorized");
        }

        return response.json();
      })
      .then((jsonResponse) => {
        spinner.classList.add("d-none");

        if (jsonResponse.data.verified === true) {
          phoneVerified.classList.remove("d-none");
          hideSiblingsElements(phoneNotVerified);

          return;
        }

        if (jsonResponse.data.attempts >= 3) {
          // Limit of verification code requests reached, display message:
          //Sorry, you reached your daily amount of SMS verifications. Try again tomorrow.

          errorsWrapper.classList.remove("d-none");
          hideSiblingsElements(errorsWrapper);

          limitVerificationError.classList.remove("d-none");
          hideSiblingsElements(limitVerificationError);

          return;
        }

        phoneNotVerified.classList.remove("d-none");
        hideSiblingsElements(phoneNotVerified);
      })
      .catch(() => {});
  });
};
